import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import TextInput from '../../../../shared/Inputs/TextInput/TextInput'
import SelectInput from '../../../../shared/Inputs/SelectInput/SelectInput'
import FileInput from '../../../../shared/Inputs/FileInput/FileInput'
import { INSTANCE, UPLOADS_ENDPOINT } from '../../../../server-parameters'
import {
  validateName,
  stringDigitsLength,
  validateString,
  smartValidateString,
} from '../../../../validate/Fiscal/Company/validateStepCompanyRus'
import { Country, Lang } from '../../../../types/common-types'
import { State } from '../../../../state-actions-reducers'
import warningImage from '../../../../images/warning.svg'
import DatePicker from '../../../../shared/Inputs/DatePicker/DatePicker'
import { getCompany } from '../../../../hooks/useCompanyCustomHook'
import styles from '../../FiscalScreenOne.module.scss'
import AddressInputs from '../../AddressInputs/AddressInputs'
import { getFiscalFullName } from '../../../../utils/TranslateFiscalRf/getFiscalFullName'
import { getFiscalShortName } from '../../../../utils/TranslateFiscalRf/getFiscalShortName'
import { foundingDocumentOptions } from '../../../../content/foundingDocument'
import InnExist from '../../../../components/IdentityExist/InnExist'
import { rfCity } from '../../../../content/cities'
import { inclineFirstname, inclineLastname, inclineMiddlename } from 'lvovich';
import cn from 'classnames'
import { getFoundingDocumentImageRf } from '../../../../utils/TranslateFiscalRf/getFoundingDocumentImage'

interface Props {
  lang: Lang;
  accountForm: any
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
}

/* REACT COMPONENT */
function CompanyScreenOne({
  lang,
  accountForm,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()

  const [focus, setFocus] = useState('')

  async function autoFill() {
    let response = await getCompany(state.F_INN),
      fullName = response.suggestions[0]?.data.name.full_with_opf,
      shortName = response.suggestions[0]?.data.name.short_with_opf,
      ogrn = response.suggestions[0]?.data.ogrn
    if (
      (fullName && !state.F_FullName) ||
      (fullName && state.F_FullName && !validateName(state.F_FullName, false))
    )
      setValue('F_FullName', processFiscalName(fullName), { shouldDirty: true })
    if (
      (shortName && !state.F_ShortName) ||
      (shortName &&
        state.F_ShortName &&
        !validateName(state.F_ShortName, false))
    )
      setValue('F_ShortName', processFiscalName(shortName), { shouldDirty: true })
    if (
      (ogrn && !state.F_OGRN) ||
      (ogrn && state.F_OGRN && !stringDigitsLength(state.F_OGRN, 13, false))
    )
      setValue('F_OGRN', ogrn, { shouldDirty: true })
  }
  useEffect(() => {
    if (state.F_INN && stringDigitsLength(state.F_INN, 10, false)) autoFill()
  }, [state.F_INN])
  const fullNameObject = getFiscalFullName(state.F_FiscalType)
  const shortNameObject = getFiscalShortName(state.F_FiscalType)
  const foundingDocumentImageObject = getFoundingDocumentImageRf(state.foundingDocument)
  const [shortName, setShortName] = useState(shortNameObject)
  const [fullName, setFullName] = useState(fullNameObject)
  const [foundingDocumentImage, setFoundingDocumentImage] = useState(foundingDocumentImageObject)
  useEffect(() => {
    setShortName(shortNameObject)
    setFullName(fullNameObject)
  }, [state.F_FiscalType])
  useEffect(()=> {
    setFoundingDocumentImage(foundingDocumentImageObject)
  }, [state.foundingDocument])
  useEffect(() => {
    setValue('F_FiscalType', '2', { shouldDirty: true })
  }, [state.countryRegistration])

  function processFiscalName(v?: string | null): string {
    if(!v) return '';
    v = v.trim()
    v = v.replace(/^["'«]+/, '').replace(/["'»]+$/, '');
    v = v.trim()
    if(!v) return '';

    let parts = v.split(" ")
    if(parts.length > 1 && ["ООО", "АО", "ПАО"].includes(parts[0].toUpperCase())) {
      parts.shift()
      v = parts.join(" ")
    }

    const bigExc = ["общество с ограниченной ответственностью", "акционерное общество", "публичное акционерное общество"]
    for(const e of bigExc){
      if(v.toLowerCase().startsWith(e)){
        const wordsCount = e.split(" ").length;
        parts = parts.slice(wordsCount)
        break;
      }
    }

    v = parts.join(" ")
    v = v.replace(/^["'«]+/, '').replace(/["'»]+$/, '').trim()
    if(!v) return '';

    const firstLetterIndex = v.search(/[A-Za-zА-Яа-яЁё]/);
    if (firstLetterIndex >= 0) {
      const ch = v[firstLetterIndex].toUpperCase();
      v = v.slice(0, firstLetterIndex) + ch + v.slice(firstLetterIndex + 1);
    }

    if (!v.startsWith('«')) {
      v = '«' + v;
    }
    if (!v.endsWith('»')) {
      v = v + '»';
    }

    return v;
  }

  useEffect(() => {
    setValue('mainCity', INSTANCE === 'msk' ? rfCity[1].value : rfCity[0].value, { shouldDirty: true })
  }, [lang])
  return (
    <>
      <div>
        <div className="form-group">
          <div className="row">
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_INN_Image}
                
                onChange={F_INN_Image => {
                  setValue('F_INN_Image', [...(getValues('F_INN_Image')||[]), F_INN_Image], { shouldDirty: true })
                }}
                value={state.F_INN_Image[state.F_INN_Image.length - 1]}
                valid={!errors.F_INN_Image}
                documentType="INN"
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-6 col-xs-12">
              {/* F_FullName */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={fullName}
                tooltip={lang === 'eng' ? 'Enter the company name without LLC, Ltd., Inc., etc.' : 'Введите только название компании, без ООО, АО, ИП и т. д.'}
                onChange={F_FullName =>
                  setValue('F_FullName', F_FullName, { shouldDirty: true })
                }
                value={state.F_FullName || ''}
                valid={
                  !errors.F_FullName
                }
                required
                onBlur={() => {
                  setFocus('')
                  const v = state.F_FullName;
                  setValue('F_FullName', processFiscalName(v), { shouldDirty: true })
                }}
                focus={focus === 'F_FullName' || undefined}
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* F_ShortName */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={shortName}
                tooltip={lang === 'eng' ? 'Enter the short company name without LLC, Ltd., Inc., etc.' : 'Введите краткое название, без ООО, АО, ИП и т. д.'}
                onChange={F_ShortName =>
                  setValue('F_ShortName', F_ShortName, { shouldDirty: true })
                }
                value={state.F_ShortName || ''}
                valid={
                  !errors.F_ShortName
                }
                required
                onBlur={() => {
                  setFocus('')
                  const v = state.F_ShortName;
                  setValue('F_ShortName', processFiscalName(v), { shouldDirty: true })
                }}
                focus={focus === 'F_ShortName' || undefined}
              />
            </div>
            {state.countryRegistration !== '398' ? (
              <div className="col-4 col-xs-12">
                <DatePicker
                  lang={lang}
                  accountForm={accountForm}
                  accountFormKey="registeredDate"
                  onChange={data => {
                    setValue('registeredDate', data, { shouldDirty: true })
                  }}
                  value={state.registeredDate}
                  focus={focus === 'registeredDate' || undefined}
                  onBlur={() => setFocus('')}
                  valid={!errors.registeredDate}
                  required
                  min={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 67)
                    )
                  }
                  max={new Date()}
                />
              </div>
            ) : null}
            <InnExist
              accountForm={accountForm}
              lang={lang}
              
              show={!state.isIdentityOk}
            />
            <div className="col-4 col-xs-12">
              {/* F_OGRN */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.F_OGRN}
                onChange={F_OGRN => {
                  setValue('F_OGRN', F_OGRN.replace(/\D/gim, '').substr(0, 13), { shouldDirty: true })
                }}
                value={state.F_OGRN || ''}
                valid={
                  stringDigitsLength(state.F_OGRN, 13, false) &&
                  !errors.F_OGRN
                }
                mask={
                  state.F_FiscalType === '1'
                    ? '999999999999999'
                    : '9999999999999'
                }
              />
            </div>
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_EGRUL_Image}
                
                onChange={F_EGRUL_Image => {
                  setValue('F_EGRUL_Image', [...(getValues('F_EGRUL_Image')||[]), F_EGRUL_Image], { shouldDirty: true })
                }}
                valid={!errors.F_EGRUL_Image}
                documentType="EGRYUL"
                value={state.F_EGRUL_Image[state.F_EGRUL_Image.length - 1]}
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_OGRN_Image}
                
                onChange={F_OGRN_Image => {
                  setValue('F_OGRN_Image', [...(getValues('F_OGRN_Image')||[]), F_OGRN_Image], { shouldDirty: true })
                }}
                valid={!errors.F_OGRN_Image}
                documentType="OGRN"
                value={state.F_OGRN_Image[state.F_OGRN_Image.length - 1]}
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Charter_Image}
                
                onChange={F_CHARTER_IMAGE => {
                  setValue('F_Charter_Image', [...(getValues('F_Charter_Image')||[]), F_CHARTER_IMAGE], { shouldDirty: true })
                }}
                valid={!errors.F_CHARTER_Image}
                documentType="STATUTE"
                value={state.F_Charter_Image[state.F_Charter_Image.length - 1]}
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Protocol_Image}
                
                onChange={F_PROTOCOL_IMAGE => {
                  setValue('F_Protocol_Image', [...(getValues('F_Protocol_Image')||[]), F_PROTOCOL_IMAGE], { shouldDirty: true })
                }}
                valid={!errors.F_PROTOCOL_Image}
                documentType="PROTOCOL"
                value={state.F_Protocol_Image[state.F_Protocol_Image.length - 1]}
                imageErrors={state.imageError}
              />
            </div>
            <div className={cn("col-6 col-xs-12", styles.inputWidthCheckbox)}>
              <div className={cn(styles.checkInputBox, styles.absoluteInput)}>
              <input
                id="hideContractInput"
                name="hideContractInput"
                type="checkBox"
                onChange={() => {
                  setValue('noLeaseAgreement', !state.noLeaseAgreement, { shouldDirty: true })
                  setValue('F_Rent_Contract_Image', [], { shouldDirty: true })
                }}
                checked={state.noLeaseAgreement}
              />
              <label htmlFor="hideContractInput">
                {accountForm.noRentContractBtnDesc[lang]}
              </label>
            </div>
              <FileInput
                disabled={state.noLeaseAgreement}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Rent_Contract_Image}
                
                onChange={F_Rent_Contract_Image => {
                  setValue('F_Rent_Contract_Image', [...(getValues('F_Rent_Contract_Image')||[]), F_Rent_Contract_Image], { shouldDirty: true })
                }}
                valid={!errors.F_Rent_Contract_Image}
                documentType="Lease"
                value={
                  state.F_Rent_Contract_Image[
                    state.F_Rent_Contract_Image.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3 className={styles.header}>
                {accountForm.Fiscal_Current_Leader_Header[lang]}
              </h3>
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headLastname}
                onChange={(headLastname: string) => {
                  setValue('headLastname', headLastname, { shouldDirty: true })
                  setValue('headLastnameGenitive', inclineLastname(headLastname, 'genitive'), { shouldDirty: true })
                }}
                value={state.headLastname || ''}
                valid={
                  state.headLastname.length > 0
                    ? smartValidateString(state.headLastname) &&
                      !errors.headLastname
                    : !errors.headLastname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headFirstname}
                onChange={(headFirstname: string) => {
                  setValue('headFirstname', headFirstname, { shouldDirty: true })
                  setValue('headFirstnameGenitive', inclineFirstname(headFirstname, 'genitive'), { shouldDirty: true })
                }}
                value={state.headFirstname || ''}
                valid={
                  state.headFirstname.length > 0
                    ? smartValidateString(state.headFirstname) &&
                      !errors.headFirstname
                    : !errors.headFirstname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.headPatronymic}
                onChange={(headPatronymic: string) => {
                  setValue('headPatronymic', headPatronymic, { shouldDirty: true })
                  setValue('headPatronymicGenitive', inclineMiddlename(headPatronymic, 'genitive'), { shouldDirty: true })
                }}
                checkLabel={accountForm.noPatronymic[lang]}
                checkValue={state.noPatronymic}
                checkChange={() => {
                  setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                  setValue('headPatronymic', '', { shouldDirty: true })
                  setValue('headPatronymicGenitive', '', { shouldDirty: true })
                }}
                value={state.headPatronymic || ''}
                valid={
                  state.headPatronymic.length > 0
                    ? smartValidateString(state.headPatronymic) &&
                      !errors.headPatronymic
                    : !errors.headPatronymic
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                className={styles.grayPlaceholder}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headLastnameGenitive}
                onChange={(headLastnameGenitive: string) =>
                  setValue('headLastnameGenitive', headLastnameGenitive, { shouldDirty: true })
                }
                value={state.headLastnameGenitive || ''}
                valid={
                  state.headLastnameGenitive.length > 0
                    ? smartValidateString(state.headLastnameGenitive) &&
                      !errors.headLastnameGenitive
                    : !errors.headLastnameGenitive
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                className={styles.grayPlaceholder}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headFirstnameGenitive}
                onChange={(headFirstnameGenitive: string) =>
                  setValue('headFirstnameGenitive', headFirstnameGenitive, { shouldDirty: true })
                }
                value={state.headFirstnameGenitive || ''}
                valid={
                  state.headFirstnameGenitive.length > 0
                    ? smartValidateString(state.headFirstnameGenitive) &&
                      !errors.headFirstnameGenitive
                    : !errors.headFirstnameGenitive
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                className={styles.grayPlaceholder}
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.headPatronymicGenitive}
                onChange={(headPatronymicGenitive: string) =>
                  setValue('headPatronymicGenitive', headPatronymicGenitive, { shouldDirty: true })
                }
                value={state.headPatronymicGenitive || ''}
                valid={
                  state.headPatronymicGenitive.length > 0
                    ? smartValidateString(state.headPatronymicGenitive) &&
                      !errors.headPatronymicGenitive
                    : !errors.headPatronymicGenitive
                }
              />
            </div>
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.Fiscal_Warning_Genitive[lang]}
                <span>
                  {' '}
                  {accountForm.Fiscal_Warning_Genitive_Example[lang]}
                </span>
              </p>
            </div>
            <div className="col-4 col-xs-12">
              <SelectInput
                testName={'foundingDocument'}
                testErrorName={'error-foundingDocument'}
                lang={lang}
                noOptionsMessage={accountForm.foundingDocument.placeholder}
                content={accountForm.foundingDocument}
                onChange={data => {
                  setValue('foundingDocument', data, { shouldDirty: true })
                }}
                value={state.foundingDocument}
                options={foundingDocumentOptions}
                valid={
                  !errors.foundingDocument &&
                  validateString(state.foundingDocument, 100)
                }
                className={styles.inputl}
              />
            </div>
            <div className="col-4 col-xs-12">
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headPosition}
                onChange={(headPosition: string) => {
                  setValue('headPosition', headPosition, { shouldDirty: true })
                  if (lang === 'eng') {
                    setValue('headPositionGenitive', headPosition, { shouldDirty: true })
                  } else {
                    setValue('headPositionGenitive', inclineFirstname(headPosition, 'genitive'), { shouldDirty: true })
                  }
                }}
                value={state.headPosition || ''}
                valid={
                  state.headPosition.length > 0
                    ? smartValidateString(state.headPosition) &&
                      !errors.headPosition
                    : !errors.headPosition
                }
              />
            </div>
            {lang !== 'eng' && (
              <div className="col-4 col-xs-12">
                <TextInput
                  className={styles.grayPlaceholder}
                  disabled={!state.agreed}
                  lang={lang}
                  content={accountForm.headPositionGenitive}
                  onChange={(headPositionGenitive: string) =>
                    setValue('headPositionGenitive', headPositionGenitive, { shouldDirty: true })
                  }
                  value={state.headPositionGenitive || ''}
                  valid={
                    state.headPositionGenitive.length > 0
                      ? smartValidateString(state.headPositionGenitive) &&
                        !errors.headPositionGenitive
                      : !errors.headPositionGenitive
                  }
                />
              </div>
            )}
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Scan_Directors_Passport}
                
                onChange={data => {
                  setValue('F_Scan_Directors_Passport', [...(getValues('F_Scan_Directors_Passport')||[]), data], { shouldDirty: true })
                }}
                valid={!errors.F_Scan_Directors_Passport}
                documentType="Passport1"
                value={
                  state.F_Scan_Directors_Passport[
                    state.F_Scan_Directors_Passport.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-8 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={foundingDocumentImage}
                
                onChange={data => {
                  setValue('foundingDocument_Image', [...(getValues('foundingDocument_Image')||[]), data], { shouldDirty: true })
                }}
                valid={!errors.foundingDocument_Image}
                documentType="FoundingDocument"
                value={
                  state.foundingDocument_Image[
                    state.foundingDocument_Image.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Scan_Directors_Passport_Address}
                
                onChange={data => {
                  setValue('F_Scan_Directors_Passport_Address', [...(getValues('F_Scan_Directors_Passport_Address')||[]), data], { shouldDirty: true })
                }}
                valid={
                  !errors.F_Scan_Directors_Passport_Address
                }
                documentType="Passport2"
                value={
                  state.F_Scan_Directors_Passport_Address[
                    state.F_Scan_Directors_Passport_Address.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.Fiscal_Warning_Scan_Directors_Passport[lang]}
              </p>
            </div>
          </div>
        </div>
        <AddressInputs
          lang={lang}
          
          accountForm={accountForm}
          addressCountries={addressCountries}
          regionCityRegistered={regionCityRegistered}
          regionCityFactual={regionCityFactual}
        />
      </div>
    </>
  )
}

export default CompanyScreenOne
